/**
 * Shared projection for one frame's `View`: rotates a unit-sphere point and
 * maps it to screen coordinates, depth, scale and visibility. Used by the
 * globe's pins and the network.
 *
 * The names are the projection's notation, kept short on purpose: the
 * shaders do the same projection on the graphics card with the same names
 * (renderer/shaders/camera.glsl). `d` is the camera's distance in globe
 * radii, `F` the focal length (`Rs·√(d²−1)`), `C` is `d² − 1`, and `kk` the
 * screen scale at a point (`F / (d − z)`, pixels per unit of the surface).
 * The globe and the shaders also use `zz`: depth from the rim (0) to the
 * point nearest the eye (1).
 */
import type { View } from './renderer/renderer'

/** A projected point on screen. The projector writes into one reused object per frame. */
export interface Projected {
  x: number
  y: number
  z: number
  kk: number
  vis: boolean
}

/**
 * Build a projector for one frame's view. The returned `project` writes into
 * and returns the same object on every call; copy any values before the next
 * call.
 */
export function projector(v: View): { project(X: number, Y: number, Z: number): Projected } {
  const { cx, cy, d } = v
  const F = v.Rs * Math.sqrt(d * d - 1)
  const C = d * d - 1
  const sl = Math.sin(v.lam)
  const c2 = Math.cos(v.lam)
  const ct = Math.cos(v.tilt)
  const st = Math.sin(v.tilt)
  const out: Projected = { x: 0, y: 0, z: 0, kk: 0, vis: false }

  return {
    project(X: number, Y: number, Z: number): Projected {
      const rx = X * c2 + Y * sl
      const x = -X * sl + Y * c2
      const y = -(Z * ct - rx * st)
      const z = rx * ct + Z * st
      const dz = z - d
      const aa = x * x + y * y + dz * dz
      const bb = 2 * d * dz
      const disc = bb * bb - 4 * aa * C
      const kk = F / (d - z)
      out.vis = disc < 0 || (-bb - Math.sqrt(disc)) / (2 * aa) > 0.999
      out.x = cx + x * kk
      out.y = cy + y * kk
      out.z = z
      out.kk = kk
      return out
    },
  }
}
